<template>
  <div class="borrowerAttributeDateMonthYear">
    <BorrowerAttributeConstructor
      :errorText="errorText"
      :helpText="helpText"
      :disclaimer="disclaimer"
    >
      <template v-slot:input>
        <div class="date-month-select">
          <div class="d-inline-flex" v-if="!hasCustomLabels">
            <p>{{ label }}</p>
            <LendioBootstrapTooltip v-if="tooltipText">
              <span
                class="borrower-attribute-tooltip px-2 blue-300"
                data-bs-toggle="tooltip"
                data-bs-placement="right"
                :title="tooltipText"
              >
                <fa-icon :icon="['fal', 'circle-info']"></fa-icon>
              </span>
            </LendioBootstrapTooltip>
          </div>
          <div class="row">
            <div class="col-12 col-lg-6">
              <div class="form-floating">
                <select
                  :id="'ba' + identifier + '-month'"
                  :name="'ba' + identifier"
                  :data-cy="question.alias + '_month'"
                  class="form-control form-select text-truncate"
                  :class="{
                    'label-visible': month || month === 0 || showLabel,
                    'is-invalid':
                      (invalidMonth && v$.month.$dirty) || !isPastDate,
                    'no-value-select': month !== 0 && !month,
                    'disabled': disabled,
                    'disabled-repressed': disabled && repressDisabledStyle
                  }"
                  v-model="month"
                  :required="questionRequired"
                  :disabled="disabled"
                  @blur="onBlur(v$, v$.month)"
                  @change="onChange(v$, v$.month)"
                  @click="setShowLabel(true)"
                >
                  <!-- For this message to display the value must equal the data value onload -->
                  <option :value="null" disabled selected>
                    {{ defaultMonthPlaceholder }}
                  </option>
                  <option v-for="(month, i) in months" :value="i" :key="i">
                    {{ month }}
                  </option>
                </select>
                <label
                  v-if="month || month === 0 || showLabel"
                  :for="'ba-' + identifier + '-month'"
                >
                  <span>{{ monthLabel }}</span>
                </label>
              </div>
              <div
                class="invalid-feedback d-block"
                v-if="mappedErrorMessages.month"
              >
                <fa-icon :icon="['fal', 'circle-exclamation']"></fa-icon>
                {{ mappedErrorMessages.month }}
              </div>
            </div>
            <div class="col-12 col-lg-6 mt-5 mt-lg-0">
              <div class="form-floating">
                <input
                  type="tel"
                  :id="'ba' + identifier + '-year'"
                  :name="'ba' + identifier"
                  :data-cy="question.alias + '_year'"
                  class="form-control text-truncate"
                  :class="{
                    'is-invalid':
                      (invalidYear && v$.year.$dirty) || !isPastDate,
                    'disabled': disabled,
                    'disabled-repressed': disabled && repressDisabledStyle
                  }"
                  v-model="year"
                  inputmode="numeric"
                  @keyup="onChange(v$, v$.year, false)"
                  @keydown.enter="onChange(v$, v$.year, true)"
                  @blur="onBlur(v$, v$.year)"
                  :disabled="disabled"
                  :required="questionRequired"
                  placeholder="Year"
                  v-maska
                  data-maska="####"
                />
                <label :for="'ba-' + identifier + '-year'">
                  <span>{{ yearLabel }}</span>
                </label>
              </div>
              <span
                class="invalid-feedback d-block"
                v-if="mappedErrorMessages.year"
              >
                <fa-icon :icon="['fal', 'circle-exclamation']"></fa-icon>
                {{ mappedErrorMessages.year }}
              </span>
            </div>
          </div>
        </div>
      </template>
    </BorrowerAttributeConstructor>
  </div>
</template>

<script>
import BorrowerAttributeConstructor from './BorrowerAttributeConstructor.vue'
import { getYear } from "date-fns";
import { format } from "date-fns";
import { toDate } from "date-fns";
import { isValid } from "date-fns";
import { isAfter } from "date-fns";
import { getMonth } from "date-fns";
import { useVuelidate } from "@vuelidate/core";
import { required, maxLength } from "@vuelidate/validators";
import { mapState } from "pinia";
import BaseBorrowerAttributeMixin from "./BaseBorrowerAttributeMixin";
import { useQuestionsStore } from "~/store/questions";

const isValidYear = (value) => {
  const yearIsFourDigits = /^\d{4}$/.test(value)
  return yearIsFourDigits && value > 1900 && value <= getYear(new Date());
};

export default {
  props: {
    disabled: Boolean,
    repressDisabledStyle: Boolean,
    displayAltOptions: Boolean,
    customButton: Object,
  },
  emits: ["invalidity", "answer"],
  setup: () => ({ v$: useVuelidate() }),
  data() {
    return {
      format: "yyyy-MM-dd",
      months: [
        "January",
        "February",
        "March",
        "April",
        "May",
        "June",
        "July",
        "August",
        "September",
        "October",
        "November",
        "December",
      ],
      year: "",
      month: null,
      showLabel: null,
    };
  },
  created: function () {
    this.$watch(
      "invalid",
      (invalid) => {
        this.$emit("invalidity", invalid);
      },
      { immediate: true }
    );

    this.hydrateDate(this.question.value)
  },
  mixins: [BaseBorrowerAttributeMixin],
  validations: {
    month: { required },
    year: {
      required,
      maxLength: maxLength("4"),
      isValidYear,
    },
  },
  computed: {
    ...mapState(useQuestionsStore, ["singleForm"]),
    identifier() {
      return this.question.id || this.question.alias
    },
    invalid() {
      return this.v$.$invalid;
    },
    invalidYear() {
      return !!this.v$.year && this.v$.year.$invalid;
    },
    invalidMonth() {
      return !!this.v$.month && this.v$.month.$invalid;
    },
    day() {
      if (this.question.alias === "business_start_date") {
        return 1;
      }
      return 5;
    },
    defaultMonthPlaceholder() {
      return this.question.monthLabel ? this.question.monthLabel : !this.month ? 'Month' : ''
    },
    monthLabel() {
      return this.question.monthLabel ? this.question.monthLabel : 'Month'
    },
    yearLabel() {
      return this.question.yearLabel ? this.question.yearLabel : 'Year'
    },
    hasCustomLabels() {
      return this.question.monthLabel && this.question.yearLabel
    },
    alternateBusinessStart() {
      return (
        this.displayAltOptions && this.question.alias === "business_start_date"
      );
    },
    nowText() {
      if (this.alternateBusinessStart) {
        return "I haven't started yet";
      }
      return this.question.nowText || "";
    },
    errorText() {
      return !Object.keys(this.mappedErrorMessages).length && !this.isPastDate
        ? "Please input a valid past date"
        : "";
    },
    mappedErrorMessages() {
      const messages = {};
      if (this.v$.year && this.v$.year.$error && this.isPastDate) {
        messages.year = "Please input a valid year";
      }
      if (this.v$.month && this.v$.month.$error) {
        messages.month = "Please select a month";
      }
      return messages;
    },
    isPastDate() {
      var newValue = new Date(this.year, this.month, this.day);

      return !isAfter(newValue, new Date());
    },
  },
  methods: {
    updateDate() {
      const yearInt = parseInt(this.year)
      if (isValidYear(yearInt) && this.month !== "") {
        var newValue = new Date(this.year, this.month, this.day);
        var checkValid = isValid(newValue);

        if (checkValid) {
          return format(newValue, this.format);
        } else {
          return null;
        }
      }
    },
    altOption() {
      this.$emit("answer", {
        value: format(new Date(), this.format),
        submitImmediately: true,
      });
    },
    onBlur(v$, changedField) {
      !this.month && this.setShowLabel(false);
      changedField.$touch(); // Make field dirty to display errors
    },
    onChange(v$, changedField, submitImmediately) {
      const date = this.updateDate();
      this.$emit("answer", {
        value: date,
        isInvalid: this.invalidYear || this.invalidMonth || !this.isPastDate,
        submitImmediately,
      });
    },
    setShowLabel(payload) {
      this.showLabel = payload;
    },
    hydrateDate(value) {
      const parseSpace = value ? value.split(' ') : ''
      const separatedDate = parseSpace[0] ? parseSpace[0].split('-') : ''
      const initialDate = toDate(new Date(separatedDate[0], (separatedDate[1] - 1), separatedDate[2]))
      const date = isValid(initialDate) ? initialDate : null
      if (date) {
        this.year = getYear(date)
        this.month = getMonth(date)
      } else {
        this.year = ''
        this.month = null
      }
    }
  },
  watch: {
    customButton: {
      handler: function (newVal) {
        if (newVal.clicked === true) {
          this.altOption();
        }
      },
    },
    "question.value": function (newVal, oldVal) {
      this.hydrateDate(newVal)
    },
  },
};
</script>

<style lang="scss" scoped>

.form-floating {
  label {
    color: $type-400;
  }
}
.form-select {
  @include media-breakpoint-down(lg) {
    padding-right: 20px !important;
  }
}

</style>
